/**
 * Creates a debounced version of the provided callback function.
 * The debounced function will delay invoking the callback until after a specified amount of time has passed since the last time it was invoked.
 *
 * Example: const onChangeDebounced = debounce(onChange, 500)
 *
 * @param callback The callback function to debounce.
 * @param delayMs The delay in milliseconds before invoking the callback after the last invocation.
 * @returns A debounced version of the callback function.
 */
export function debounce<T extends (...args: any[]) => any>(
  callback: T,
  delayMs: number,
) {
  let timeout: NodeJS.Timeout | undefined;
  return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
    clearTimeout(timeout);
    timeout = setTimeout(() => {
      callback.apply(this, args);
    }, delayMs);
  };
}

/**
 * Debounce séquentiel sans délai :
 * - Aucune exécution en parallèle.
 * - Pendant un run, on mémorise seulement le dernier appel (coalescing).
 * - À la fin, on relance immédiatement s'il y a eu au moins un appel pendant le run.
 *
 * @template {(...args: any[]) => any|Promise<any>} F
 * @param {F} fn  Fonction à exécuter (ex: refetch).
 * @returns {(...args: Parameters<F>) => Promise<ReturnType<F>>}  Fonction debounced.
 */
export type DebouncedSequential<F extends (...args: any[]) => any> = {
  (...args: Parameters<F>): Promise<Awaited<ReturnType<F>>>;
  cancel(): void;
  isRunning(): boolean;
};

export function debounceSequential<F extends (...args: any[]) => any>(
  fn: F,
): DebouncedSequential<F> {
  let running = false;
  let queued = false;
  let lastArgs: Parameters<F> | undefined;
  let lastThis: ThisParameterType<F> | undefined;
  let pendingResolvers: Array<{
    resolve: (value: Awaited<ReturnType<F>>) => void;
    reject: (reason?: unknown) => void;
  }> = [];

  const run = async (): Promise<void> => {
    if (running) {
      queued = true;
      return;
    }
    running = true;

    const args = lastArgs!;
    const ctx = lastThis!;
    const resolvers = pendingResolvers;
    pendingResolvers = [];

    try {
      const result = await Promise.resolve(fn.apply(ctx, args));
      for (const { resolve } of resolvers)
        resolve(result as Awaited<ReturnType<F>>);
    } catch (err) {
      for (const { reject } of resolvers) reject(err);
    } finally {
      running = false;
      if (queued) {
        queued = false;
        run();
      }
    }
  };

  const debounced: DebouncedSequential<F> = function (
    this: ThisParameterType<F>,
    ...args: Parameters<F>
  ) {
    lastArgs = args;
    lastThis = this;
    return new Promise<Awaited<ReturnType<F>>>((resolve, reject) => {
      pendingResolvers.push({ resolve, reject });
      if (!running) run();
      else queued = true;
    });
  } as unknown as DebouncedSequential<F>;

  debounced.cancel = () => {
    queued = false;
    const err = new Error('Debounce annulé');
    for (const { reject } of pendingResolvers.splice(0)) reject(err);
  };

  debounced.isRunning = () => running;

  return debounced;
}
